import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { REPORTS_QUERY_KEY } from '@/hooks/queries/reports/use-reports';
import { useAsyncWsRequest } from '@/hooks/use-async-ws-request';
import type { BopWsMessage } from '@/lib/websocket';
import type { ReportType } from '@/services/api/reports';

export type ReportPush = Extract<BopWsMessage, { type: 'reportReady' | 'reportFailed' }>;
export type ReportReadyPush = Extract<ReportPush, { type: 'reportReady' }>;

// The report workers run with a 15-minute Lambda timeout, so nothing arrives after this
const DEFAULT_TIMEOUT_MS = 15 * 60_000;

export const isReportPushFor = (message: BopWsMessage, reportType: ReportType): message is ReportPush =>
  (message.type === 'reportReady' || message.type === 'reportFailed') && message.reportType === reportType;

export interface UseAsyncReportOptions {
  reportType: ReportType;
  /** Starts the run; the outcome arrives as a push, not in this response */
  request: () => Promise<unknown>;
  /** Human name used in toasts, e.g. "DAC8 audit report" */
  label: string;
  /** Wired to the success toast's View action */
  onReady?: (message: ReportReadyPush) => void;
  timeoutMs?: number;
}

export interface AsyncReport {
  isGenerating: boolean;
  requestReport: () => Promise<void>;
}

/**
 * Server-generated report that lands in S3 and is announced over the websocket. On `reportReady`
 * the listing query for that report type is invalidated so the new file appears; on
 * `reportFailed` the wait ends with the error code. Sibling of useAsyncFileExport.
 */
export const useAsyncReport = ({ reportType, request, label, onReady, timeoutMs }: UseAsyncReportOptions): AsyncReport => {
  const queryClient = useQueryClient();
  // Stable identities keep the core's message effect from re-running on every render
  const isMatch = useCallback((message: BopWsMessage): message is ReportPush => isReportPushFor(message, reportType), [reportType]);
  const onMatch = useCallback(
    (message: ReportPush) => {
      if (message.type === 'reportFailed') {
        toast.error(`${label} failed: ${message.error}`);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: [...REPORTS_QUERY_KEY, reportType] });
      toast.success(`${label} ready`, onReady ? { action: { label: 'View', onClick: () => onReady(message) } } : undefined);
    },
    [label, onReady, queryClient, reportType],
  );
  const { isPending, start } = useAsyncWsRequest({
    request,
    isMatch,
    onMatch,
    timeoutMs: timeoutMs ?? DEFAULT_TIMEOUT_MS,
    timeoutMessage: `${label} is still being generated. It will appear in the list when ready.`,
    requestErrorMessage: `Failed to request the ${label}`,
  });

  return { isGenerating: isPending, requestReport: start };
};
